import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash2, Edit3, ChevronRight, FolderTree } from "lucide-react";

const slugify = (s) => (s || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const flatten = (nodes, depth, out) => {
  for (const n of nodes) {
    out.push({ ...n, __depth: depth });
    if (n.children && n.children.length) flatten(n.children, depth + 1, out);
  }
};

const AdminCategories = () => {
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState({ name: "", slug: "", description: "", image_url: "", seo_title: "", seo_description: "", parent_id: null, is_active: true });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/categories");
      setTree(data);
    } catch (e) { toast.error("Failed to load", { description: e.response?.data?.detail || "" }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const flatRows = [];
  flatten(tree, 0, flatRows);

  const openCreate = (parent) => {
    setForm({ name: "", slug: "", description: "", image_url: "", seo_title: "", seo_description: "", parent_id: parent ? parent.id : null, is_active: true });
    setDialog({ mode: "create" });
  };

  const openEdit = (cat) => {
    setForm({ name: cat.name || "", slug: cat.slug || "", description: cat.description || "", image_url: cat.image_url || "", seo_title: cat.seo_title || "", seo_description: cat.seo_description || "", parent_id: cat.parent_id || null, is_active: cat.is_active !== false });
    setDialog({ mode: "edit", cat });
  };

  const remove = async (cat) => {
    if (!window.confirm(`Delete category "${cat.name}"? Children become root.`)) return;
    try { await api.delete(`/admin/categories/${cat.id}`); toast.success("Category deleted"); load(); }
    catch (e) { toast.error("Delete failed", { description: e.response?.data?.detail || "" }); }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, slug: form.slug ? slugify(form.slug) : slugify(form.name) };
      if (dialog.mode === "create") {
        await api.post("/admin/categories", payload);
        toast.success("Category created");
      } else {
        await api.patch(`/admin/categories/${dialog.cat.id}`, payload);
        toast.success("Category updated");
      }
      setDialog(null); load();
    } catch (e) { toast.error("Save failed", { description: e.response?.data?.detail || "" }); }
    finally { setSaving(false); }
  };

  return (
    <AdminLayout title="Categories" actions={
      <Button data-testid="cat-new-btn" onClick={() => openCreate(null)} className="rounded-none bg-accent hover:bg-accent/90"><Plus size={14} className="mr-2" /> New category</Button>
    }>
      <div className="border border-border bg-card">
        <div className="px-4 py-3 border-b border-border bg-background flex items-center gap-2 text-[11px] uppercase tracking-widest text-muted-foreground">
          <FolderTree size={12} /> Category tree
        </div>
        {loading && <div className="p-8 text-center text-muted-foreground text-sm">Loading…</div>}
        {!loading && flatRows.length === 0 && <div className="p-12 text-center text-sm text-muted-foreground" data-testid="categories-empty">No categories yet — create your first one.</div>}
        {!loading && flatRows.map((cat) => {
          const depth = cat.__depth;
          const padding = 16 + depth * 24;
          return (
            <div
              key={cat.id}
              className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border hover:bg-background/60"
              style={{ paddingLeft: padding + "px" }}
              data-testid={"cat-row-" + cat.id}
            >
              <div className="flex items-center gap-2 min-w-0">
                {depth > 0 && <ChevronRight size={12} className="text-muted-foreground shrink-0" />}
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{cat.name}</div>
                  <div className="text-[11px] text-muted-foreground truncate">/{cat.slug} · {cat.is_active ? "active" : "hidden"}</div>
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <button data-testid={"cat-add-child-" + cat.id} onClick={() => openCreate(cat)} className="text-xs text-accent hover:underline">Add child</button>
                <button data-testid={"cat-edit-" + cat.id} onClick={() => openEdit(cat)} className="text-xs text-muted-foreground hover:text-accent"><Edit3 size={12} /></button>
                <button data-testid={"cat-delete-" + cat.id} onClick={() => remove(cat)} className="text-xs text-muted-foreground hover:text-destructive"><Trash2 size={12} /></button>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="rounded-none max-w-lg">
          <DialogHeader><DialogTitle>{dialog && dialog.mode === "create" ? "New category" : "Edit category"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="overline text-muted-foreground text-[10px]">Name</Label><Input data-testid="cat-name-input" className="rounded-none mt-1" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value, slug: f.slug || slugify(e.target.value) }))} /></div>
            <div><Label className="overline text-muted-foreground text-[10px]">Slug (URL)</Label><Input data-testid="cat-slug-input" className="rounded-none mt-1" value={form.slug} onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))} /></div>
            <div><Label className="overline text-muted-foreground text-[10px]">Description</Label><Textarea data-testid="cat-desc-input" rows={2} className="rounded-none mt-1" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} /></div>
            <div><Label className="overline text-muted-foreground text-[10px]">Image URL</Label><Input data-testid="cat-image-input" className="rounded-none mt-1" value={form.image_url} onChange={(e) => setForm((f) => ({ ...f, image_url: e.target.value }))} /></div>
            <div><Label className="overline text-muted-foreground text-[10px]">SEO title</Label><Input data-testid="cat-seo-title" className="rounded-none mt-1" value={form.seo_title} onChange={(e) => setForm((f) => ({ ...f, seo_title: e.target.value }))} /></div>
            <div><Label className="overline text-muted-foreground text-[10px]">SEO description</Label><Textarea data-testid="cat-seo-desc" rows={2} className="rounded-none mt-1" value={form.seo_description} onChange={(e) => setForm((f) => ({ ...f, seo_description: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setDialog(null)}>Cancel</Button>
            <Button data-testid="cat-save-btn" className="rounded-none bg-accent hover:bg-accent/90" disabled={saving} onClick={save}>{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminCategories;
